import React, { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btn, btnGold, btnGreen, btnRed, Chip, Empty, ErrorBox, field, Loading, REQUEST_CHIP, useLoad, when } from "@/components/nds/windows/finance/parts";

function RequestForm({ residentId, onDone, onCancel }) {
  const [f, setF] = useState({ request_type: "withdrawal", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await finApi.post("/savings/requests", { ...f, resident_id: residentId, amount: Number(f.amount) }); toast.success("Request submitted"); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="fin-savings-form" className="p-4 border border-nex-gold/30 rounded-sm bg-white/[0.02] flex flex-wrap gap-2 items-center">
      <select value={f.request_type} onChange={set("request_type")} data-testid="fin-savings-form-type" className={field}>
        <option value="withdrawal">Withdrawal</option><option value="transfer">Transfer to Cash in Hand</option>
      </select>
      <input type="number" min="0.01" step="0.01" required value={f.amount} onChange={set("amount")} placeholder="Amount" data-testid="fin-savings-form-amount" className={`${field} w-32`} />
      <input required value={f.reason} onChange={set("reason")} placeholder="Reason" data-testid="fin-savings-form-reason" className={`${field} flex-1 min-w-[180px]`} />
      <button type="button" onClick={onCancel} data-testid="fin-savings-form-cancel" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
      <button type="submit" disabled={busy || !f.amount || !f.reason.trim()} data-testid="fin-savings-form-submit" className={btnGold}>Submit</button>
    </form>
  );
}

function RequestRow({ req, isSuper, onChanged }) {
  const [denying, setDenying] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (path, body, msg) => {
    setBusy(true);
    try { await finApi.patch(`/savings/requests/${req.id}/${path}`, body); toast.success(msg); onChanged(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  const [tone, label] = REQUEST_CHIP[req.status];
  return (
    <li data-testid={`fin-savings-req-${req.id}`} className="p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-nex-silver font-medium capitalize">{req.request_type}</span>
        <Chip tone={tone} data-testid={`fin-savings-req-status-${req.id}`}>{label}</Chip>
        <span className="text-xs text-nex-muted truncate max-w-[260px]">{req.reason}</span>
        <span className="ml-auto font-mono text-sm text-nex-gold">{money(req.amount)}</span>
      </div>
      <div className="text-[11px] text-nex-muted">
        Requested {when(req.requested_at)} by {req.requested_by?.name || "—"}
        {req.reviewed_at && <> · {label} {when(req.reviewed_at)} by {req.reviewed_by?.name}</>}
        {req.review_notes && <> · <span className="text-nex-silver/80">{req.review_notes}</span></>}
      </div>
      {req.status === "pending" && (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={busy} onClick={() => act("approve", {}, "Approved · posted to ledger")} data-testid={`fin-savings-approve-${req.id}`} className={btnGreen}>Approve</button>
          {!denying ? <button type="button" disabled={busy} onClick={() => setDenying(true)} data-testid={`fin-savings-deny-${req.id}`} className={btnRed}>Deny</button> : (
            <>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Review notes (required)" data-testid={`fin-savings-deny-notes-${req.id}`} className={`${field} flex-1 min-w-[180px] !py-1.5`} />
              <button type="button" disabled={busy || !notes.trim()} onClick={() => act("deny", { review_notes: notes }, "Request denied")} data-testid={`fin-savings-deny-confirm-${req.id}`} className={btnRed}>Confirm deny</button>
              <button type="button" onClick={() => setDenying(false)} data-testid={`fin-savings-deny-cancel-${req.id}`} className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
            </>
          )}
          {isSuper && <button type="button" disabled={busy} onClick={() => act("void", {}, "Request voided")} data-testid={`fin-savings-void-${req.id}`} className={`${btn} border-white/15 text-nex-muted hover:text-nex-silver`}>Void</button>}
        </div>
      )}
    </li>
  );
}

export default function SavingsRequestsTab({ resident }) {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [state, load] = useLoad(() => finApi.get("/savings/requests", { resident_id: resident.id }), [resident.id]);
  return (
    <div data-testid="fin-savings-requests-tab" className="p-5 space-y-3">
      {!adding ? (
        <button type="button" onClick={() => setAdding(true)} data-testid="fin-savings-new" className={`${btnGold} inline-flex items-center gap-1.5`}><Plus className="h-3 w-3" /> New request</button>
      ) : <RequestForm residentId={resident.id} onCancel={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      <p className="text-[11px] text-nex-muted">If savings can't cover an approved amount, savings stops at $0.00 and the shortfall is carried to the resident's line of credit.</p>
      {state.loading && !state.data ? <Loading testid="fin-savings-requests-loading" />
        : state.error ? <ErrorBox testid="fin-savings-requests-error" error={state.error} onRetry={load} />
        : state.data.requests.length ? (
          <ul data-testid="fin-savings-requests-list" className="border border-white/10 rounded-sm divide-y divide-white/5">
            {state.data.requests.map((q) => <RequestRow key={q.id} req={q} isSuper={user?.role === "super_admin" || user?.is_super_admin} onChanged={load} />)}
          </ul>
        ) : <Empty testid="fin-savings-requests-empty">No savings requests yet.</Empty>}
    </div>
  );
}
